/**
 * Keyed write queue — de-duplicated, debounced persistence for inline editors.
 *
 * WHY (LOT TYPING-AND-SETLIST-LAYOUT, 2026-11-04). Every inline field of the show
 * editor (hotel, rooming, guest, distance, transport leg, contact, schedule row,
 * day info) called `await apiUpdate…()` on EVERY keystroke and then re-wrote its
 * local state from the response. Two consequences, both measured on DEV:
 *
 *   1. one PUT per character — a 20-char hotel name meant 20 PUTs, 20
 *      `activity_log` rows and 20 WebSocket broadcasts;
 *   2. the response landed ~80 ms later and replaced the array item, which
 *      re-rendered the controlled inputs from a copy that was one keystroke
 *      behind → letters typed during the round-trip vanished, the caret jumped.
 *
 * The fix is the same everywhere: apply the edit to LOCAL state synchronously
 * (optimistic), and let this queue persist only the LAST payload of each key,
 * once the user has stopped typing. A reply never re-writes the field.
 *
 * Usage:
 *   const queue = useRef(createWriteQueue()).current;
 *   useEffect(() => () => queue.flushAll(), [queue]);   // nothing lost on unmount
 *   queue.schedule(String(row.id), async () => { await apiUpdate…(latest); });
 *
 * `schedule()` replaces the pending task of the same key, so the closure always
 * carries the newest payload. Errors are the task's business (reportSaveError) —
 * the queue never swallows a rejection silently into an unhandled promise.
 */

/** Default idle delay before a pending edit is persisted (ms). */
export const WRITE_DEBOUNCE_MS = 500;

export interface KeyedWriteQueue {
  /** Queue (or replace) the write of `key`; runs after `delayMs` of silence. */
  schedule(key: string, task: () => Promise<unknown> | unknown): void;
  /** Run the pending write of `key` right now (blur, delete, toggle). */
  flush(key: string): void;
  /** Run every pending write (unmount, page leave). */
  flushAll(): void;
  /** Drop the pending write of `key` without running it (row deleted). */
  cancel(key: string): void;
  /** Is a write queued for `key`? */
  has(key: string): boolean;
}

type Task = () => Promise<unknown> | unknown;

export function createWriteQueue(delayMs: number = WRITE_DEBOUNCE_MS): KeyedWriteQueue {
  const timers = new Map<string, ReturnType<typeof setTimeout>>();
  const tasks = new Map<string, Task>();

  const run = (key: string) => {
    const timer = timers.get(key);
    if (timer !== undefined) clearTimeout(timer);
    timers.delete(key);
    const task = tasks.get(key);
    tasks.delete(key);
    if (!task) return;
    // A rejected write must not become an unhandled rejection; callers report it.
    void Promise.resolve()
      .then(task)
      .catch(() => { /* reported by the task itself */ });
  };

  return {
    schedule(key, task) {
      tasks.set(key, task);
      const previous = timers.get(key);
      if (previous !== undefined) clearTimeout(previous);
      timers.set(key, setTimeout(() => run(key), delayMs));
    },
    flush(key) { run(key); },
    flushAll() { for (const key of Array.from(tasks.keys())) run(key); },
    cancel(key) {
      const timer = timers.get(key);
      if (timer !== undefined) clearTimeout(timer);
      timers.delete(key);
      tasks.delete(key);
    },
    has(key) { return tasks.has(key); },
  };
}
